/**
 * Forgot Password Modal
 *
 * Modal for requesting password reset email.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { Mail, CheckCircle } from 'lucide-react';
import { forgotPasswordSchema } from '@bstock/shared';
import type { ForgotPasswordInput } from '@bstock/shared';
import { Modal, ModalFooter, Button, Input } from '@/components/ui';
import { authService } from '@/api/services/auth.service';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ isOpen, onClose }: ForgotPasswordModalProps) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    setError(null);
    try {
      await authService.forgotPassword(data.email);
      setIsSuccess(true);
    } catch (err) {
      // Even on error, show success for security (anti-enumeration)
      setIsSuccess(true);
    }
  };

  const handleClose = () => {
    reset();
    setIsSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Mot de passe oublie"
      size="sm"
    >
      {isSuccess ? (
        <div className="text-center py-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-dark-500 mb-2">
            Email envoye !
          </h3>
          <p className="text-sm text-neutral-600 mb-4">
            Si cette adresse est associee a un compte, vous recevrez un email avec
            un lien de reinitialisation.
          </p>
          <p className="text-xs text-neutral-500 mb-6">
            Pensez a verifier vos spams.
          </p>
          <Button variant="primary" onClick={handleClose} className="w-full">
            Fermer
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-neutral-600">
            Entrez votre adresse email et nous vous enverrons un lien pour
            reinitialiser votre mot de passe.
          </p>

          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-dark-500 mb-1">
              Email
            </label>
            <Input
              type="email"
              {...register('email')}
              placeholder="email@exemple.com"
              error={errors.email?.message}
            />
          </div>

          <ModalFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" isLoading={isSubmitting}>
              <Mail className="w-4 h-4" />
              Envoyer le lien
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
}
